import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Tooltip, message } from "antd";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import { useUpdateChallenge } from "../../../../queries/game-pass/useUpdateChallenge";
import { useUpdateSeason } from "../../../../queries/game-pass/useUpdateSeason";
import {
  Button,
  Input,
  RebetLoader,
  SegmentedControl,
  TabSelector,
  ToggleSwitch,
  type SegmentedControlItem,
  type TabSelectorItem,
} from "../../../shared";

import {
  createEmptyChallenge,
  duplicateTaskDraft,
  mapSequentialToProgressionMode,
  reorderChallenges,
  sanitizeIntegerInput,
  type ChallengeDraft,
  type ChallengesValues,
  type SeasonTiersValue,
  type TaskDraft,
  type TaskSubmissionModeDefaults,
} from "../createSeasonData";
import type { SeasonTaskSubmissionMode } from "../../../../types/gamePass";

import ChallengeCard, {
  type ChallengeCardDragProps,
} from "./ChallengeCard";
import { useTaskClipboard } from "./taskClipboard";

import styles from "./ChallengesAndTasks.module.css";

/**
 * Tier selector value. The visible tabs are driven by the Step 1 season
 * path checkboxes (`free_path_enabled` / `premium_path_enabled`) so the
 * wizard only shows the paths the admin actually enabled on season create.
 * Challenge-level filtering has not landed yet, so the tab selection is
 * currently UI-only — both paths render the same list until BE ships
 * per-challenge tier assignment.
 */
type ChallengeTier = "FREE" | "PREMIUM";

const matchesActiveTier = (
  challenge: ChallengeDraft,
  activeTier: ChallengeTier,
): boolean =>
  challenge.pathAvailability === "BOTH" ||
  challenge.pathAvailability === activeTier;

/**
 * Where this editor is being rendered.
 *
 *  - `"wizard"` (default) — the Create Season wizard. The "Create
 *    Challenges" intro copy renders as a section header, and the
 *    Free / Premium chooser is shown as a brand-underline `TabSelector`
 *    strip below it.
 *  - `"embedded"` — Season Details for an editable (Scheduled / Draft)
 *    season. The page header already names the section ("Challenges"),
 *    so the create-style intro is suppressed. The Free / Premium
 *    chooser still ships, but as the pill `SegmentedControl` from the
 *    Figma so the in-card surface stays compact and reads as a
 *    filter rather than a section header.
 */
export type ChallengesAndTasksVariant = "wizard" | "embedded";

export type ChallengesAndTasksProps = {
  values: ChallengesValues;
  /** Receives the full snapshot after any internal change so the parent
   *  can validate the step and persist it later via the API layer. */
  onChange: (next: ChallengesValues) => void;
  /** Defaults to `"wizard"` so the long-standing Create Season call site
   *  keeps its current chrome. Pass `"embedded"` when mounting inside
   *  the Season Details editable view to drop the intro and swap the
   *  underline tabs for the pill SegmentedControl. */
  variant?: ChallengesAndTasksVariant;
  /** Id of the draft season the user is editing. Forwarded into every
   *  `ChallengeCard` so the per-card "Create Task" flow can fire
   *  `POST /game-pass-admin/v2/admin/tasks` with the right `season_id`. When
   *  omitted the per-card create flow falls back to local-only — used
   *  by the SeasonDetails embedded variant until the season-detail
   *  endpoint is wired. */
  seasonId?: string | null;
  /** Which season paths are enabled from Step 1. Defaults to both so
   *  existing embedded/editor surfaces keep their current behaviour. */
  enabledTiers?: SeasonTiersValue;
  /** Default submission modes from Step 1 / General — forwarded to new tasks. */
  sportsbookSubmissionMode?: SeasonTaskSubmissionMode;
  casinoSubmissionMode?: SeasonTaskSubmissionMode;
};

const ChallengesAndTasks: FC<ChallengesAndTasksProps> = ({
  values,
  onChange,
  variant = "wizard",
  seasonId,
  enabledTiers = { free: true, premium: true },
  sportsbookSubmissionMode = "AUTO",
  casinoSubmissionMode = "AUTO",
}) => {
  const {
    sequential,
    sameXpReward = false,
    sameXpAmount = "",
    challenges,
  } = values;

  // When the season-level "Same XP reward for all tasks" toggle is on AND an
  // amount has been entered, every task's XP field is locked to this value
  // inside the Create / Edit Task modal. Empty / toggle-off leaves per-task XP
  // freely editable (undefined = no lock).
  const forcedXpAmount =
    sameXpReward && sameXpAmount.trim() ? sameXpAmount.trim() : undefined;
  const defaultSubmissionModes = useMemo(
    () => ({
      sportsbook: sportsbookSubmissionMode,
      casino: casinoSubmissionMode,
    }),
    [casinoSubmissionMode, sportsbookSubmissionMode],
  );
  const [activeTier, setActiveTier] = useState<ChallengeTier>(
    enabledTiers.free ? "FREE" : "PREMIUM",
  );
  const isEmbedded = variant === "embedded";

  // Cross-season task clipboard (sessionStorage-backed via `useTaskClipboard`).
  // A task copied here survives navigation, so it can be pasted into a challenge
  // in any other season opened in the same tab — the paste creates it under the
  // target card's season/challenge ids. The stored copy drops the transient
  // image `File` and keeps `imgKey` / `imageUrl`, so the pasted task reuses the
  // same S3 image.
  const { copiedTask, copyTask, clearClipboard } = useTaskClipboard();

  const handleCopyTask = useCallback(
    (task: TaskDraft) => {
      copyTask(task);
      message.success(AppStrings.TASK_COPIED_SUCCESS);
    },
    [copyTask],
  );

  // Persists the new `sort_order` of any *saved* challenge after a drag.
  const updateChallengeMutation = useUpdateChallenge();
  const updateSeasonMutation = useUpdateSeason();
  // Drives the blocking loader over the list while a reorder is being saved.
  // Tracked locally (not off the mutation's `isPending`) because a single
  // reorder can fan out into several concurrent PUTs and we want one overlay
  // that stays up until every one of them resolves.
  const [isPersistingReorder, setIsPersistingReorder] = useState(false);

  // Root section element, used to scope the post-add DOM lookup that scrolls a
  // freshly-added (or re-surfaced) challenge into view.
  const containerRef = useRef<HTMLElement>(null);
  // Id of the challenge to scroll into view + focus on the next render. Set
  // when the admin adds / duplicates a challenge, or clicks "Add Challenge"
  // while an unsaved draft is still open (in which case we surface the open
  // draft instead of stacking a second empty form). Cleared by the effect
  // once the scroll/focus has run.
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // At most one never-saved challenge draft may be open at a time. A second
  // "Add Challenge" click while a draft is unsaved would pile up empty forms,
  // so we re-surface the existing draft instead of creating another.
  const unsavedDraftId = useMemo(
    () => challenges.find((challenge) => !challenge.isSaved)?.id ?? null,
    [challenges],
  );
  const hasUnsavedDraft = unsavedDraftId !== null;

  // Latest `values` snapshot for async revert paths — avoids rolling back a
  // stale closure if the admin edits challenges while a progression-mode PUT
  // is still in flight.
  const latestValuesRef = useRef(values);
  latestValuesRef.current = values;

  // After a challenge is added / duplicated (or an open draft is re-surfaced),
  // scroll its card into view and focus the name field so the admin lands
  // directly on the form regardless of how far down the list it sits. Depends
  // on `challenges` too so the effect re-runs once the new card is in the DOM.
  useEffect(() => {
    if (!pendingFocusId) return;
    const container = containerRef.current;
    const cardEl = container?.querySelector<HTMLElement>(
      `[data-testid="create-season-challenge-${pendingFocusId}"]`,
    );
    if (cardEl) {
      cardEl.scrollIntoView?.({ behavior: "smooth", block: "center" });
      cardEl
        .querySelector<HTMLInputElement>('[data-testid="challenge-card-name"]')
        ?.focus?.();
    }
    setPendingFocusId(null);
  }, [pendingFocusId, challenges]);

  // Shared item list — `TabSelectorItem<T>` and `SegmentedControlItem<T>`
  // are structurally identical, so the same readonly array drives both the
  // wizard (TabSelector) and the embedded Season Details view
  // (SegmentedControl). Step 1's path checkboxes determine which tiers are
  // visible here; if only one path is enabled, only that single tab renders.
  const tierItems = useMemo<
    ReadonlyArray<TabSelectorItem<ChallengeTier> & SegmentedControlItem<ChallengeTier>>
  >(
    () => {
      const next: Array<
        TabSelectorItem<ChallengeTier> & SegmentedControlItem<ChallengeTier>
      > = [];
      if (enabledTiers.free) {
        next.push({ id: "FREE", label: AppStrings.CHALLENGES_TIER_FREE });
      }
      if (enabledTiers.premium) {
        next.push({
          id: "PREMIUM",
          label: AppStrings.CHALLENGES_TIER_PREMIUM,
        });
      }
      return next.length > 0
        ? next
        : [{ id: "FREE", label: AppStrings.CHALLENGES_TIER_FREE }];
    },
    [enabledTiers.free, enabledTiers.premium],
  );

  useEffect(() => {
    if (tierItems.some((item) => item.id === activeTier)) return;
    setActiveTier(tierItems[0].id);
  }, [activeTier, tierItems]);

  /** Apply `mutator` to the challenge with `id` (no-op if no match). */
  const updateChallenge = useCallback(
    (id: string, mutator: (current: ChallengeDraft) => ChallengeDraft) => {
      onChange({
        ...values,
        challenges: values.challenges.map((c) =>
          c.id === id ? mutator(c) : c,
        ),
      });
    },
    [onChange, values],
  );

  const handleAddChallenge = useCallback(() => {
    // Don't stack empty forms — if a draft is still unsaved, just bring it
    // back into view (and focus it) instead of creating another one.
    if (unsavedDraftId) {
      setPendingFocusId(unsavedDraftId);
      return;
    }
    const nextIndex = challenges.length + 1;
    const next = createEmptyChallenge(
      nextIndex,
      AppStrings.CHALLENGE_LABEL_PREFIX,
      activeTier,
    );
    onChange({ ...values, challenges: [...challenges, next] });
    setPendingFocusId(next.id);
  }, [activeTier, challenges, onChange, unsavedDraftId, values]);

  const handleSequentialChange = useCallback(
    async (next: boolean) => {
      const previous = sequential;
      onChange({ ...latestValuesRef.current, sequential: next });

      if (!seasonId) return;

      try {
        await updateSeasonMutation.mutateAsync({
          seasonId,
          body: { progression_mode: mapSequentialToProgressionMode(next) },
        });
      } catch (error) {
        onChange({ ...latestValuesRef.current, sequential: previous });
        message.error(
          getApiErrorMessage(error, "Failed to update progression mode"),
        );
      }
    },
    [onChange, seasonId, sequential, updateSeasonMutation],
  );

  const handleSameXpRewardChange = useCallback(
    (next: boolean) => {
      onChange({ ...values, sameXpReward: next });
    },
    [onChange, values],
  );

  const handleSameXpAmountChange = useCallback(
    (raw: string) => {
      onChange({ ...values, sameXpAmount: sanitizeIntegerInput(raw) });
    },
    [onChange, values],
  );

  const handleChallengeChange = useCallback(
    (next: ChallengeDraft) => {
      updateChallenge(next.id, () => next);
    },
    [updateChallenge],
  );

  const handleChallengeSave = useCallback(
    (id: string) => {
      updateChallenge(id, (current) => ({
        ...current,
        isSaved: true,
        isExpanded: false,
      }));
    },
    [updateChallenge],
  );

  const handleChallengeEdit = useCallback(
    (id: string) => {
      updateChallenge(id, (current) => ({ ...current, isExpanded: true }));
    },
    [updateChallenge],
  );

  const handleChallengeClose = useCallback(
    (id: string) => {
      // "Close" on the editor card behaves like a discard:
      //   - never-saved challenges are removed entirely
      //   - previously-saved challenges just collapse back to the summary view
      const target = values.challenges.find((c) => c.id === id);
      if (!target) return;
      if (!target.isSaved) {
        onChange({
          ...values,
          challenges: values.challenges.filter((c) => c.id !== id),
        });
        return;
      }
      updateChallenge(id, (current) => ({ ...current, isExpanded: false }));
    },
    [onChange, updateChallenge, values],
  );

  const handleChallengeDelete = useCallback(
    (id: string) => {
      onChange({
        ...values,
        challenges: values.challenges.filter((c) => c.id !== id),
      });
    },
    [onChange, values],
  );

  const handleChallengeDuplicate = useCallback(
    (id: string) => {
      // Preserve the "one unsaved draft at a time" invariant — duplicating
      // while a draft is still open would stack a second never-saved form, so
      // re-surface the existing draft instead (mirrors `handleAddChallenge`).
      if (unsavedDraftId) {
        setPendingFocusId(unsavedDraftId);
        return;
      }
      const source = values.challenges.find((c) => c.id === id);
      if (!source) return;
      const nextIndex = values.challenges.length + 1;
      const duplicated: ChallengeDraft = {
        ...source,
        id: createEmptyChallenge(nextIndex, AppStrings.CHALLENGE_LABEL_PREFIX)
          .id,
        persistedId: undefined,
        pathAvailability: activeTier,
        name: `${source.name}${AppStrings.CHALLENGE_DUPLICATE_NAME_SUFFIX}`,
        // Re-key every copied task as a fresh, not-yet-persisted draft so the
        // duplicate carries the source's tasks (and their images) end-to-end:
        // each keeps its configuration but loses the source's server id, and
        // the challenge's save flow then creates them under the new challenge.
        // Without this the copy would alias the original's tasks — duplicate
        // React keys, plus edits hitting the original's `/tasks/{task_id}`.
        tasks: source.tasks.map((task, taskIndex) =>
          duplicateTaskDraft(task, taskIndex),
        ),
        isExpanded: true,
        isSaved: false,
      };
      onChange({ ...values, challenges: [...values.challenges, duplicated] });
      setPendingFocusId(duplicated.id);
    },
    [activeTier, onChange, unsavedDraftId, values],
  );

  const visibleChallenges = useMemo(
    () =>
      challenges.filter((challenge) => matchesActiveTier(challenge, activeTier)),
    [activeTier, challenges],
  );

  // ─── Drag-and-drop wiring ──────────────────────────────────────────────
  // Two sensors per the established repo pattern (Configurations/
  // SportsbookFM/SortingTable): pointer for mouse / touch and keyboard
  // for accessible reordering (focus the handle, Space to pick up,
  // arrow keys to move, Space to drop). `sortableKeyboardCoordinates`
  // teaches the KeyboardSensor how to translate arrow keys into the
  // sortable list's row deltas.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a small pointer movement before a drag is recognised so
      // a click on the drag handle (which we don't currently use for
      // anything else) doesn't accidentally start a drag.
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // `SortableContext` needs the items as a plain id array (or objects
  // with `id`). Keep this memoised so it only changes when the
  // challenges list changes — dnd-kit caches the registration against
  // this reference.
  const challengeIds = useMemo(
    () => visibleChallenges.map((c) => c.id),
    [visibleChallenges],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      // Ignore drops that land while a previous reorder is still saving —
      // the overlay blocks the pointer, but a queued keyboard drop could
      // otherwise race the in-flight PUTs.
      if (isPersistingReorder) return;
      const { active, over } = event;
      // No-op when the user drops outside a sortable item, or drops
      // back onto the original slot. `reorderChallenges` also guards
      // these cases, but skipping the spread / callback here saves a
      // re-render on the common "click without dragging" case.
      if (!over || active.id === over.id) return;
      const nextVisible = reorderChallenges(
        visibleChallenges,
        String(active.id),
        String(over.id),
      );
      if (nextVisible === visibleChallenges) return;

      // Splice the reordered visible subset back into the full challenges
      // array, preserving the slots of any challenge hidden by the active
      // tier filter. `nextChallenges` is the new canonical order.
      let visibleIndex = 0;
      const nextChallenges = values.challenges.map((challenge) =>
        matchesActiveTier(challenge, activeTier)
          ? nextVisible[visibleIndex++]
          : challenge,
      );

      // Optimistically apply the new order so the list reflows immediately.
      onChange({ ...values, challenges: nextChallenges });

      // Persist the new zero-based `sort_order` for every *saved* challenge
      // whose position actually changed. `sort_order` is a single shared
      // ordering across the whole season, so it MUST be derived from the
      // global `challenges` index — not the tier-filtered visible index.
      // Using the visible index would let a reorder in one tier assign
      // sort_order values that collide with challenges hidden in another
      // tier (FE-1628 review). Unsaved drafts (no `persistedId`) are
      // local-only and pick up their order on first create, so they're
      // skipped here.
      const previousIndexById = new Map(
        values.challenges.map((challenge, index) => [challenge.id, index]),
      );
      const updates = nextChallenges
        .map((challenge, index) => ({ challenge, index }))
        .filter(
          ({ challenge, index }) =>
            Boolean(challenge.persistedId) &&
            previousIndexById.get(challenge.id) !== index,
        );

      if (updates.length === 0) return;

      setIsPersistingReorder(true);
      try {
        await Promise.all(
          updates.map(({ challenge, index }) =>
            updateChallengeMutation.mutateAsync({
              challengeId: challenge.persistedId as string,
              seasonId,
              body: { sort_order: index },
            }),
          ),
        );
        message.success(AppStrings.CHALLENGE_REORDER_SUCCESS);
      } catch (error) {
        // Roll back to the last server-confirmed order on failure so the UI
        // never shows an order the backend didn't accept.
        onChange(values);
        message.error(
          getApiErrorMessage(error, AppStrings.CHALLENGE_REORDER_FAILED),
        );
      } finally {
        setIsPersistingReorder(false);
      }
    },
    [
      activeTier,
      isPersistingReorder,
      onChange,
      seasonId,
      updateChallengeMutation,
      values,
      visibleChallenges,
    ],
  );

  // Embedded mode strips the local title, so `aria-labelledby` would
  // dangle. Fall back to an `aria-label` pointing at the same string the
  // parent Season Details page already renders as its heading.
  const sectionA11yProps = isEmbedded
    ? { "aria-label": AppStrings.SEASON_DETAILS_CHALLENGES_HEADING }
    : { "aria-labelledby": "create-season-challenges-title" };

  return (
    <section
      ref={containerRef}
      className={styles.container}
      data-testid="create-season-challenges"
      {...sectionA11yProps}
    >
      {/* The "Create Challenges" intro copy is wizard-only — when this
          editor is embedded inside Season Details, the page already
          names the section and the admin is editing an existing
          season. The Free / Premium chooser still ships in both
          surfaces, but it renders as an underline `TabSelector` in the
          wizard (long header strip) and as a pill `SegmentedControl`
          in the embedded card (per the Figma — Game Pass → Season
          Details). */}
      {!isEmbedded ? (
        <>
          <header className={styles.intro}>
            <h2
              id="create-season-challenges-title"
              className={styles.title}
            >
              {AppStrings.CREATE_CHALLENGES}
            </h2>
            <p className={styles.description}>
              {AppStrings.CREATE_CHALLENGES_DESCRIPTION}
            </p>
          </header>

          <TabSelector<ChallengeTier>
            items={tierItems}
            value={activeTier}
            onChange={setActiveTier}
            ariaLabel={AppStrings.CHALLENGES_TIER_TABS_ARIA}
            data-testid="create-season-challenges-tier-tabs"
            className={styles.tierTabs}
          />
        </>
      ) : (
        <SegmentedControl<ChallengeTier>
          items={tierItems}
          value={activeTier}
          onChange={setActiveTier}
          ariaLabel={AppStrings.CHALLENGES_TIER_TABS_ARIA}
          data-testid="create-season-challenges-tier-segments"
          className={styles.tierSegments}
        />
      )}

      <div className={styles.controlRow}>
        <div className={styles.toggleGroup}>
          <label
            className={styles.toggleLabel}
            data-testid="create-season-sequential-toggle"
          >
            <ToggleSwitch
              value={sequential}
              onChange={handleSequentialChange}
              tone="info"
            />
            <span>
              {AppStrings.USERS_MUST_COMPLETE_CHALLENGES_SEQUENTIALLY}
            </span>
          </label>
          <label
            className={styles.toggleLabel}
            data-testid="create-season-same-xp-toggle"
          >
            <ToggleSwitch
              value={sameXpReward}
              onChange={handleSameXpRewardChange}
              tone="info"
            />
            <span>{AppStrings.SAME_XP_REWARD_FOR_ALL_TASKS}</span>
            <Tooltip title={AppStrings.SAME_XP_REWARD_FOR_ALL_TASKS_INFO}>
              <InfoCircleOutlined
                className={styles.toggleInfoIcon}
                data-testid="create-season-same-xp-info"
              />
            </Tooltip>
          </label>
        </div>
        <Button
          intent="primary"
          onClick={handleAddChallenge}
          title={
            hasUnsavedDraft
              ? AppStrings.ADD_CHALLENGE_FINISH_DRAFT_FIRST
              : undefined
          }
          data-testid="create-season-add-challenge"
        >
          {AppStrings.ADD_CHALLENGE}
        </Button>
      </div>

      {sameXpReward && (
        <div className={styles.sameXpField}>
          <Input
            value={sameXpAmount}
            inputMode="numeric"
            placeholder={AppStrings.SAME_XP_REWARD_AMOUNT_PLACEHOLDER}
            onChange={(e) => handleSameXpAmountChange(e.target.value)}
            className={styles.sameXpInput}
            data-testid="create-season-same-xp-amount"
          />
          <span
            className={styles.sameXpHint}
            data-testid="create-season-same-xp-hint"
          >
            {AppStrings.SAME_XP_REWARD_HINT}
          </span>
        </div>
      )}

      {visibleChallenges.length > 0 && (
        <>
          {/* When "Users must complete challenges sequentially" is on, the
              challenge order is meaningful and must stay locked — the admin
              can't reorder via drag. We render the same cards without the
              drag wiring (no DndContext / SortableContext, `drag` omitted so
              each card's handle stays inert) instead of merely disabling the
              sensors, so there is no draggable affordance at all. */}
          {sequential ? (
            <ul
              className={styles.list}
              data-testid="create-season-challenges-list"
              aria-label={AppStrings.CHALLENGE_LIST_ARIA}
            >
              {visibleChallenges.map((challenge, index) => (
                <li
                  key={challenge.id}
                  className={styles.listItem}
                  data-testid={`create-season-challenge-${challenge.id}`}
                >
                  <ChallengeCard
                    challenge={challenge}
                    index={index + 1}
                    onChange={handleChallengeChange}
                    onSave={() => handleChallengeSave(challenge.id)}
                    onEdit={() => handleChallengeEdit(challenge.id)}
                    onClose={() => handleChallengeClose(challenge.id)}
                    onDelete={() => handleChallengeDelete(challenge.id)}
                    onDuplicate={() => handleChallengeDuplicate(challenge.id)}
                    seasonId={seasonId}
                    forcedXpAmount={forcedXpAmount}
                    defaultSubmissionModes={defaultSubmissionModes}
                    copiedTask={copiedTask}
                    onCopyTask={handleCopyTask}
                    onClearClipboard={clearClipboard}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.listWrap}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={challengeIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul
                    className={`${styles.list} ${
                      isPersistingReorder ? styles.listReordering : ""
                    }`}
                    data-testid="create-season-challenges-list"
                    aria-label={AppStrings.CHALLENGE_LIST_ARIA}
                  >
                    {visibleChallenges.map((challenge, index) => (
                      <SortableChallengeCard
                        key={challenge.id}
                        challenge={challenge}
                        index={index + 1}
                        onChange={handleChallengeChange}
                        onSave={() => handleChallengeSave(challenge.id)}
                        onEdit={() => handleChallengeEdit(challenge.id)}
                        onClose={() => handleChallengeClose(challenge.id)}
                        onDelete={() => handleChallengeDelete(challenge.id)}
                        onDuplicate={() =>
                          handleChallengeDuplicate(challenge.id)
                        }
                        seasonId={seasonId}
                        forcedXpAmount={forcedXpAmount}
                        defaultSubmissionModes={defaultSubmissionModes}
                        copiedTask={copiedTask}
                        onCopyTask={handleCopyTask}
                        onClearClipboard={clearClipboard}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>

              {/* Blocking loader shown while the new order is persisted. The
                  overlay captures pointer events so the admin can't start
                  another drag until every sort_order PUT resolves. */}
              {isPersistingReorder && (
                <div
                  className={styles.reorderOverlay}
                  data-testid="create-season-challenges-reorder-loader"
                  role="status"
                  aria-live="polite"
                  aria-label={AppStrings.CHALLENGE_REORDER_SAVING}
                >
                  <RebetLoader height={48} width={48} />
                </div>
              )}
            </div>
          )}

          {/* Dashed full-width "+ Add Challenge" row — per Figma, this only
              appears once the list has at least one challenge. The header
              CTA is the only entry point for the empty state. */}
          <button
            type="button"
            className={styles.addChallengeRow}
            onClick={handleAddChallenge}
            title={
              hasUnsavedDraft
                ? AppStrings.ADD_CHALLENGE_FINISH_DRAFT_FIRST
                : undefined
            }
            data-testid="create-season-add-challenge-row"
          >
            <PlusOutlined
              className={styles.addChallengeIcon}
              aria-hidden="true"
            />
            <span>{AppStrings.ADD_CHALLENGE}</span>
          </button>
        </>
      )}
    </section>
  );
};

export default ChallengesAndTasks;

// ─── Sortable adapter ────────────────────────────────────────────────────

/** Wraps `ChallengeCard` so it participates in the parent's
 *  `<SortableContext>`. Calls `useSortable` for this single row,
 *  derives the inline transform style + listeners bundle that
 *  `ChallengeCard.tsx` consumes, and forwards every other prop
 *  through. Lives in this file (not `ChallengeCard.tsx`) so the card
 *  stays free of any `@dnd-kit/*` imports — locked-mode call sites
 *  in `SeasonDetails` and isolated unit tests don't pay the cost. */
type SortableChallengeCardProps = {
  challenge: ChallengeDraft;
  index: number;
  onChange: (next: ChallengeDraft) => void;
  onSave: () => void;
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  seasonId?: string | null;
  forcedXpAmount?: string;
  defaultSubmissionModes?: TaskSubmissionModeDefaults;
  copiedTask?: TaskDraft | null;
  onCopyTask?: (task: TaskDraft) => void;
  onClearClipboard?: () => void;
};

const SortableChallengeCard: FC<SortableChallengeCardProps> = ({
  challenge,
  ...rest
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: challenge.id });

  const drag: ChallengeCardDragProps = {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transformStyle: {
      transform: CSS.Transform.toString(transform),
      transition,
    },
    isDragging,
  };

  return (
    <li
      className={styles.listItem}
      data-testid={`create-season-challenge-${challenge.id}`}
    >
      <ChallengeCard challenge={challenge} drag={drag} {...rest} />
    </li>
  );
};
